'use client';

import { useMemo } from 'react';
import { api } from 'trpc/react';

export const useWishlistIds = () => {
    const query = api.wishlist.getIds.useQuery(void 0, { staleTime: 30_000 });
    const set = useMemo(() => new Set(query.data ?? []), [query.data]);

    return {
        ids: query.data ?? [],
        set,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
    };
};

export const useToggleWishlist = (productId: string) => {
    const utils = api.useUtils();

    const mutation = api.wishlist.toggle.useMutation({
        onMutate: async vars => {
            await utils.wishlist.getIds.cancel();

            const prev = utils.wishlist.getIds.getData() ?? [];

            utils.wishlist.getIds.setData(void 0, curr => {
                const list = curr ? [...curr] : [];
                const idx = list.indexOf(vars.productId);
                if (idx >= 0) {
                    list.splice(idx, 1);
                } else {
                    list.push(vars.productId);
                }
                return list;
            });

            return { prev };
        },
        onError: (_err, _vars, ctx) => {
            if (ctx?.prev) {
                utils.wishlist.getIds.setData(void 0, ctx.prev);
            }
        },
        onSettled: async () => {
            await utils.wishlist.getIds.invalidate();
            await utils.wishlist.getAll.invalidate();
        },
    });

    return {
        toggle: () => mutation.mutate({ productId }),
        isPending: mutation.status === 'pending',
    };
};

export const useRemoveFromWishlist = (productId: string) => {
    const utils = api.useUtils();

    const mutation = api.wishlist.remove.useMutation({
        onMutate: async vars => {
            await Promise.all([utils.wishlist.getIds.cancel(), utils.wishlist.getAll.cancel()]);

            const prevIds = utils.wishlist.getIds.getData() ?? [];
            const prevList = utils.wishlist.getAll.getData();

            utils.wishlist.getIds.setData(void 0, curr => {
                const list = curr ? [...curr] : [];
                const idx = list.indexOf(vars.productId);
                if (idx >= 0) list.splice(idx, 1);
                return list;
            });

            if (prevList) {
                utils.wishlist.getAll.setData(void 0, data => {
                    if (!data) return data;
                    return { ...data, items: data.items.filter(i => i.product.id !== vars.productId) };
                });
            }

            return { prevIds, prevList };
        },
        onError: (_err, _vars, ctx) => {
            if (ctx?.prevIds) utils.wishlist.getIds.setData(void 0, ctx.prevIds);
            if (ctx?.prevList) utils.wishlist.getAll.setData(void 0, ctx.prevList);
        },
        onSettled: async () => {
            await Promise.all([utils.wishlist.getIds.invalidate(), utils.wishlist.getAll.invalidate()]);
        },
    });

    return {
        remove: () => mutation.mutate({ productId }),
        isPending: mutation.status === 'pending',
    };
};
